<template>
  <div :class="customClass + ' v2-view-recap-container'">
    <template v-if="isBusy || isBusyResend">
      <div class="table-loader">
        <Preloader />
      </div>
    </template>
    <template v-else-if="currentStep === 5">
      <div class="v2-success-view">
        <div class="success-header">
          <div class="success-icon-badge">
            <font-awesome-icon icon="check" />
          </div>
          <h1>Merci pour votre demande de devis !</h1>
          <p>Votre devis Auto a été envoyé à votre client avec succès.</p>
        </div>

        <div class="success-main-content">
          <div class="document-download-card">
            <div class="doc-info">
              <div class="doc-icon">
                <font-awesome-icon icon="file-pdf" />
              </div>
              <div class="doc-details">
                <h3>Votre Devis</h3>
                <span>Format PDF • Prêt à imprimer</span>
              </div>
            </div>
            <button type="button" class="btn-download-pdf" @click="showPdf">
              <font-awesome-icon icon="eye" />
              Aperçu
            </button>
          </div>

          <div class="success-actions">
            <button type="button" class="btn-link-action" @click="restartFresh">
              <font-awesome-icon icon="plus-circle" />
              Souscrire à une autre garantie
            </button>
            <router-link to="/devis" class="btn-link-action secondary">
              <font-awesome-icon icon="th-list" />
              Voir mes devis
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- Header Section -->
      <header class="v2-view-header">
        <div class="header-left">
          <!-- <router-link to="/devis" class="v2-back-link" v-if="showRetourButton">
            <font-awesome-icon icon="arrow-circle-left" />
            Retour
          </router-link> -->
          <h1>Devis en ligne</h1>
        </div>
        <div class="header-right" v-if="!currentStep">
          <router-link :to="'/garantie-auto-mensualisee-v2/' + insuranceData.id" class="v2-modify-btn">
            <font-awesome-icon icon="pencil-alt" />
            Modifier
          </router-link>
        </div>
      </header>

      <div class="v2-view-grid">
        <!-- Main Column -->
        <main class="v2-main-col">
          <!-- Warnings -->
          <div class="alert alert-warning mb-2"
            v-if="insurance.details && insurance.details.registration && numberOfDuplicates > 0">
            <font-awesome-icon icon="exclamation-triangle" class="mr-2" />
            Attention ! Un contrat a déjà été saisi avec ce nom et ce numéro d’immatriculation.
          </div>

          <div class="v2-view-accordion-stack">
            <!-- Client Info Accordion -->
            <section class="v2-recap-accordion-item" :class="{ 'is-expanded': ownerExpanded }"
              v-if="insurance && insurance.client">
              <header class="accordion-header" @click="ownerExpanded = !ownerExpanded">
                <div class="header-title">
                  <font-awesome-icon icon="user-circle" />
                  <h2>Vos coordonnées</h2>
                </div>
                <i class="v2-chevron-icon"></i>
              </header>
              <div class="accordion-content" v-show="ownerExpanded">
                <div class="card-body grid-adaptive">
                  <div class="info-item" v-if="+insurance.client.civility !== CLIENT_CIVILITY.SOCIETE.ID">
                    <label>Civilité :</label>
                    <span class="info-val">{{ parseCivility(insurance.client.civility) }}</span>
                  </div>
                  <div class="info-item" v-else>
                    <label>Société :</label>
                    <span class="info-val">{{ insurance.client.name_of_signatory }}</span>
                  </div>

                  <div class="info-item">
                    <label>
                      {{ +insurance.client.civility !== CLIENT_CIVILITY.SOCIETE.ID ? 'Nom :' : 'Nom du signataire : ' }}
                    </label>
                    <span class="info-val">{{ insurance.client.last_name || "" }}</span>
                  </div>

                  <div class="info-item" v-if="insurance.client.first_name">
                    <label>
                      {{
                        +insurance.client.civility !== CLIENT_CIVILITY.SOCIETE.ID ? 'Prénom :' : 'Prénom du signataire: '
                      }}
                    </label>
                    <span class="info-val">{{ insurance.client.first_name || "" }}</span>
                  </div>

                  <div class="info-item">
                    <label>Email :</label>
                    <span class="info-val">{{ insurance.client.email || "" }}</span>
                  </div>

                  <div class="info-item">
                    <label>Téléphone :</label>
                    <span class="info-val">{{ insurance.client.tel || "" }}</span>
                  </div>

                  <div class="info-item" v-if="insurance.client.fax">
                    <label>Fax :</label>
                    <span class="info-val">{{ insurance.client.fax || "" }}</span>
                  </div>

                  <div class="info-item">
                    <label>Adresse :</label>
                    <span class="info-val text-capitalize">{{ insurance.client.address || "" }}, {{ insurance.client.zip
                      ||
                      "" }} {{ insurance.client.city || "" }}</span>
                  </div>

                  <div class="info-item"
                    v-if="insurance.client && insurance.client.birthday && insurance.client.birthday != '1900-01-01 00:00:00'">
                    <label>Date de naissance :</label>
                    <span class="info-val">{{ parseBirthDate(insurance.client.birthday) }}</span>
                  </div>

                  <!-- <div class="info-item" v-if="insurance.client && insurance.client.iban">
                    <label>IBAN :</label>
                    <span class="info-val">{{ insurance.client.iban }}</span>
                  </div> -->
                </div>
              </div>
            </section>

            <!-- Vehicle Info Accordion -->
            <section class="v2-recap-accordion-item" :class="{ 'is-expanded': vehicleExpanded }"
              v-if="insurance.details">
              <header class="accordion-header" @click="vehicleExpanded = !vehicleExpanded">
                <div class="header-title">
                  <font-awesome-icon icon="car" />
                  <h2>Votre Véhicule</h2>
                </div>
                <i class="v2-chevron-icon"></i>
              </header>
              <div class="accordion-content" v-show="vehicleExpanded">
                <div class="card-body grid-adaptive">
                  <div class="info-item">
                    <label>Immatriculation :</label>
                    <span class="info-val plate-style">{{ insurance.details.registration || "" }}</span>
                  </div>

                  <div class="info-item">
                    <label>Garantie :</label>
                    <span class="info-val">{{ parseValue(insurance.details.garantie, INSURANCE_AUTO) }}</span>
                  </div>

                  <div class="info-item">
                    <label>Durée :</label>
                    <span class="info-val">{{ parseValue(insurance.details.duration, AUTO_DURATION) }}</span>
                  </div>

                  <div class="info-item">
                    <label>Marque :</label>
                    <span class="info-val">{{ insurance.details.car_brand }}</span>
                  </div>

                  <div class="info-item">
                    <label>Modèle :</label>
                    <span class="info-val">{{ insurance.details.modele_porteur || "" }}</span>
                  </div>

                  <!-- <div class="info-item">
                    <label>Puissance réelle :</label>
                    <span class="info-val">{{ insurance.details.hp_real || "" }} DIN</span>
                  </div> -->

                  <div class="info-item">
                    <label>Puissance fiscale :</label>
                    <span class="info-val">{{ insurance.details.fiscal_power || "" }} CV</span>
                  </div>

                  <div class="info-item">
                    <label>Kilométrage :</label>
                    <span class="info-val">{{ insurance.details.km_real || "" }} KM</span>
                  </div>

                  <div class="info-item">
                    <label>Date de 1ère mise en circulation :</label>
                    <span class="info-val">{{ parseSaleDate(insurance.details) }}</span>
                  </div>

                  <div class="info-item">
                    <label>Date d'effet :</label>
                    <span class="info-val">{{ moment(insurance.details.effective_date).format("DD/MM/Y") }}</span>
                  </div>

                  <div class="info-item">
                    <label>Numéro de série :</label>
                    <span class="info-val">{{ insurance.details.serial_number || "" }}</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- Info & Disclaimer Accordion -->
            <section class="v2-recap-accordion-item" :class="{ 'is-expanded': infoExpanded }">
              <header class="accordion-header" @click="infoExpanded = !infoExpanded">
                <div class="header-title">
                  <font-awesome-icon icon="info-circle" />
                  <h2>Durée et Prise d'effet</h2>
                </div>
                <i class="v2-chevron-icon"></i>
              </header>
              <div class="accordion-content" v-show="infoExpanded">
                <div class="card-body">
                  <div class="v2-warranty-info-text">
                    <div class="v2-selected-plan-banner" v-if="insurance.details">
                      Vous avez choisi l'assurance auto
                      <strong>{{ parseValue(insurance.details.garantie, INSURANCE_AUTO) }}</strong>
                      pour une durée de
                      <strong>{{ parseValue(insurance.details.duration, AUTO_DURATION) }}</strong>
                    </div>
                    <p v-if="mechanicCode != '705223'">
                      Sous r&eacute;serve du paiement de la prime et de l'acceptation de la garantie par
                      <strong>Dynassurances</strong>,
                      la garantie s'applique suivant la dur&eacute;e pr&eacute;cis&eacute;e aux conditions
                      particuli&egrave;res du contrat
                      et prendra fin automatiquement &agrave; 24 heures jour pour jour selon les dates indiqu&eacute;es
                      sur le certificat de garantie.
                    </p>
                    <p v-else>
                      Sous r&eacute;serve du paiement de la prime et de l'acceptation de la garantie,
                      la garantie s'applique suivant la dur&eacute;e pr&eacute;cis&eacute;e aux conditions
                      particuli&egrave;res du contrat
                      et prendra fin automatiquement &agrave; 24 heures jour pour jour selon les dates indiqu&eacute;es
                      sur le certificat de garantie.
                    </p>

                    <p class="font-weight-bold mt-3">Cependant la garantie prendra fin de plein droit avant son terme :
                    </p>
                    <ul class="v2-feature-list">
                      <li>
                        <font-awesome-icon icon="caret-right" />
                        <span>en cas de destruction du v&eacute;hicule, quelle qu'en soit la cause, par
                          r&eacute;siliation,</span>
                      </li>
                      <li>
                        <font-awesome-icon icon="caret-right" />
                        <span>en cas de d&eacute;ch&eacute;ance du b&eacute;n&eacute;ficiaire de la garantie pour non
                          respect des prescriptions du constructeur sur l'usage pour lequel le v&eacute;hicule est
                          con&ccedil;u.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <!-- Coverage Accordion -->
            <section class="v2-recap-accordion-item" :class="{ 'is-expanded': guaranteesExpanded }">
              <header class="accordion-header" @click="guaranteesExpanded = !guaranteesExpanded">
                <div class="header-title">
                  <font-awesome-icon icon="shield-alt" />
                  <h2>Garanties accordées</h2>
                </div>
                <i class="v2-chevron-icon"></i>
              </header>
              <div class="accordion-content" v-show="guaranteesExpanded">
                <div class="card-body">
                  <div class="v2-plan-accordion">
                    <div class="v2-accordion-item is-expanded">
                      <div class="v2-accordion-content">
                        <Accordes :insurance="insurance" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- IBAN section - Commented as IBAN is not prefilled in the stripe -->
            <!-- <section class="v2-recap-accordion-item iban-section" :class="{ 'is-expanded': ibanExpanded }">
              <header class="accordion-header" @click="ibanExpanded = !ibanExpanded">
                <div class="header-title">
                  <font-awesome-icon icon="university" />
                  <h2>Coordonnées bancaires</h2>
                </div>
                <i class="v2-chevron-icon"></i>
              </header>

              <div class="accordion-content" v-show="ibanExpanded">
                <div class="card-body">
                  <div class="v2-plan-accordion">
                    <div class="v2-accordion-item is-expanded">
                      <div class="v2-accordion-content">

                        <div class="iban-update-info mb-3">
                          <p class="text-muted mb-0">
                            Vous pouvez mettre à jour les coordonnées bancaires du client en toute sécurité.
                          </p>
                        </div>

                        <div class="row">
                          <div class="col-md-8">
                            <input type="text" class="redesign-input" v-model="insurance.iban"
                              @input="handleIbanInputView" maxlength="33"
                              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" />
                          </div>

                          <div class="col-md-4 d-flex align-items-end">
                            <button type="button" class="btn-primary-outline w-100" @click="updateIban"
                              :disabled="isUpdatingIban">
                              <span v-if="isUpdatingIban">
                                Mise à jour...
                              </span>
                              <span v-else>
                                Mettre à jour l’IBAN
                              </span>
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section> -->
          </div>
        </main>

        <!-- Sidebar -->
        <aside class="v2-sticky-sidebar">
          <div class="v2-price-card">
            <div class="v2-price-header">
              <div class="v2-price-label text-uppercase">Prime mensuelle T.T.C. (&euro;) *</div>
              <div class="v2-price-amount" v-if="getMonthlyPrice">{{ getMonthlyPrice }}</div>
              <div class="alert alert-danger p-2 rem-12" v-else>
                Les critères du véhicule ne permettent pas de souscrire à la garantie choisie.
              </div>
              <p class="price-note">* Frais de dossier et taxe attentats inclus</p>
            </div>

            <div class="v2-price-details" v-if="insurance && insurance.assistance_price > 0">
              <div class="detail-row">
                <span>Dont pack assistance</span>
                <strong>
                  {{ roundEURWithoutSymbol(insurance.assistance_price).replace(".", ",") }} &euro;
                </strong>
              </div>
            </div>

            <div class="v2-actions-stack">
              <!-- Subscribe Button -->
              <button type="button" class="btn-success-outline" v-if="getMonthlyPrice"
                @click.prevent="getPrice ? checkSubscription(insurance, resendEmail) : confirmSubscription()">
                <font-awesome-icon icon="rocket" />
                Envoyer devis par email
              </button>

              <!-- Save Button -->
              <button type="button" class="v2-btn-block outline" @click.prevent="getFrontPdf()">
                <font-awesome-icon icon="download" />
                Sauvegarder sur mon ordinateur
              </button>

              <!-- Print Button -->
              <button type="button" class="v2-btn-block outline"
                @click.prevent="mechanicCode != '705223' ? getFrontPdf(true) : getFrontPdfWL(true)">
                <font-awesome-icon icon="print" />
                Imprimer le détail de la garantie
              </button>

              <!-- Inline Commission Accordion -->
              <div v-if="showCommissionAccordion" class="v2-commission-inline-editor">
                <div class="commission-editor-title">Ajustement de commission</div>
                <div class="commission-editor-body">
                  <template v-if="isBusyCommission">
                    <div class="text-center p-2">
                      <Preloader scale="0.5" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="commission-value-display">
                      <span>COMMISSION :</span>
                      <strong>{{ commissionSlider.value }} €</strong>
                    </div>

                    <div class="commission-range-wrapper">
                      <div class="range-info">
                        <span>{{ commissionSlider.min }}€</span>
                        <input type="range" v-model="commissionSlider.value" :min="commissionSlider.min"
                          :max="commissionSlider.max" :step="commissionSlider.step" @input="calcInlineMonthlyPrice" />
                        <span>{{ commissionSlider.max }}€</span>
                      </div>
                    </div>

                    <!-- <div class="commission-impact-display">
                      <span>NOUVEAU MENSUEL :</span>
                      <strong>{{ inlineMonthlyPrice }} €</strong>
                    </div> -->

                    <div class="form-row m-0 p-0">
                      <button @click="cancelInlineCommission" type="button"
                        class="btn-secondary-outline">Annuler</button>
                      <button @click="submitInlineCommission" type="button" class="btn-primary-outline"
                        :disabled="isBusyCommissionSubmit">
                        <span v-if="isBusyCommissionSubmit">Validation...</span>
                        <span v-else>Valider</span>
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <hr class="my-2">


              <button type="button" class="v2-btn-block outline" @click.prevent="toggleCommissionAccordion"
                v-if="!showCommissionAccordion && (!$store.getters.isContact || canEditCommission)">
                <font-awesome-icon icon="percent" />
                Changement commission
              </button>

              <button type="button" class="btn-secondary-outline" @click="closeQuote()">
                Fermer
              </button>
            </div>

            <div class="v2-price-footer">
              <div class="v2-contact-info">
                <span>CONTACTER OU SOUSCRIRE :</span>
                <p class="mb-0 fs-11"><a href="tel:+33148895757">01 48 89 57 57</a></p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </template>

    <PdfModal v-show="pdfModal" @close="closePdfModal" :quote="this.insurance" :is-shown="pdfModal" />
  </div>
</template>

<script>
import PdfModal from "@/components/Garantie/PdfModal";
import Preloader from "@/components/Preloader";
import garantieFieldsList from "@/mixins/garantieFieldsLists";
import Accordes from "../GarantiesAccordesAuto";
import {
  AUTO_DURATION,
  AUTO_ENGINE,
  AUTO_POWER_KW,
  AUTO_TYPE,
  INSURANCE_AUTO
} from "@/constants/insuranceAuto";
import insuranceMethods from "@/mixins/insuranceMethods";
import { CLIENT_CIVILITY } from "@/constants/client";
import pdfPreviewMixin from '@/mixins/pdfPreviewMixin';

export default {
  name: "GarantieAutoMensualiseeView",
  components: {
    PdfModal,
    Preloader,
    Accordes,
  },
  mixins: [garantieFieldsList, insuranceMethods, pdfPreviewMixin],
  props: {
    currentStep: {
      type: Number,
      required: true
    },
    customClass: {
      type: String,
      default: ''
    },
    insuranceData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      isBusy: false,
      isBusyResend: false,
      isBusyCommission: false,
      isBusyCommissionSubmit: false,
      pdfModal: false,
      subscriptionModal: false,
      selectedInsurance: 0,
      insurance: {
        address: null,
        category: null,
        city: null,
        civility: null,
        email: null,
        first_name: null,
        last_name: null,
        tel: null,
        zip: null,
        details: null
      },
      showRetourButton: false,
      AUTO_ENGINE,
      AUTO_POWER_KW,
      AUTO_TYPE,
      INSURANCE_AUTO,
      AUTO_DURATION,
      CLIENT_CIVILITY,
      pdfWindow: null,
      numberOfDuplicates: 0,
      hashModal: (+new Date()).toString(36),
      showCommissionAccordion: false,
      commissionSlider: {
        min: 0,
        max: 80,
        step: 10,
        value: 0
      },
      inlineMonthlyPrice: 0,
      originalMonthlyPrice: null,
      commissionFetched: false,
      duration_to_months: {
        3: 12,
        4: 24,
        5: 36,
        6: 48
      },
      ownerExpanded: true,
      vehicleExpanded: true,
      infoExpanded: false,
      guaranteesExpanded: false,
      ibanExpanded: false,
      isUpdatingIban: false,
    };
  },
  created() {
    if (this.insuranceData) {
      this.getQuoteInfo(this.insuranceData.id);
    } else if (this.$route.params.id) {
      this.getQuoteInfo(this.$route.params.id);
    }
  },
  computed: {
    canEditCommission() {
      return this.$store.getters.getAuthUserInfo ? this.$store.getters.getAuthUserInfo.can_edit_commission : false;
    },
    printManyPages() {
      return (
        this.insurance &&
        this.insurance.details &&
        +this.insurance.details.garantie != 3
      );
    },
    getDownloadLinkConfirmationPdf() {
      if (this.insurance.id)
        return this.getApiAuthUrl(
          "garantie/confirmation-pdf/" + this.insurance.id
        );

      return "";
    },
    getDownloadLink() {
      if (this.insurance.id) {
        return this.getApiAuthUrl("garantie/generate-pdf/" + this.insurance.id);
      }

      return "";
    },
    getDownloadLinkWL() {
      if (this.insurance.id) {
        return this.getApiAuthUrl(
          "garantie/generate-pdfWL/" + this.insurance.id
        );
      }

      return "";
    },
    getDownloadLinkCopyPdf() {
      if (this.insurance.id)
        return this.getApiAuthUrl(
          "garantie/front-copy-pdf/" + this.insurance.id
        );

      return "";
    },
    getDownloadLinkCopyPdfWL() {
      if (this.insurance.id)
        return this.getApiAuthUrl(
          "garantie/front-copy-pdfWL/" + this.insurance.id
        );

      return "";
    },
    getPrice() {
      return this.insurance.price
        ? (Math.round(parseFloat(this.insurance.price) * 100) / 100).toFixed(2).replace(".", ",") +
        " €"
        : "";
    },
    getMonthlyPrice() {
      let price = this.showCommissionAccordion && this.inlineMonthlyPrice
        ? this.inlineMonthlyPrice
        : this.insurance.monthly_price;

      return price
        ? (Math.round(parseFloat(price) * 100) / 100).toFixed(2).replace(".", ",") +
        " €"
        : "";
    },
    getGarantiePrice() {
      return this.insurance.price && +this.insurance.price > 0;
    },
    mechanicCode() {
      let code =
        this.$store.state.userInfo && this.$store.state.userInfo.mechanic
          ? this.$store.state.userInfo.mechanic.code
          : null;
      return code ? code : "";
    },
    mechanicClientPayment() {
      let client_payment =
        this.$store.state.userInfo && this.$store.state.userInfo.mechanic
          ? this.$store.state.userInfo.mechanic.client_payment
          : null;
      return client_payment ? client_payment : "";
    }
  },
  methods: {
    showPdf() {
      this.show_pdf_mensualisee(this.insurance.id);
      // this.restartFresh();
    },
    async updateIban() {

      if (!this.insurance.id) {
        this.showErrorNotification("Devis introuvable.");
        return;
      }

      if (!this.insurance.iban) {
        this.showErrorNotification("Veuillez renseigner un IBAN valide.");
        return;
      }

      let cleanIban = this.insurance.iban.replace(/\s/g, '');
      if (cleanIban.length !== 27) {
        this.showErrorNotification("L'IBAN doit contenir exactement 27 caractères.");
        return;
      }

      this.isUpdatingIban = true;

      try {

        const payload = {
          iban: cleanIban
        };

        const url = `garantie/auto/${this.insurance.id}/update-iban`;

        const response = await this.apiPost(url, payload);

        if (response.data.status === "success") {

          this.showSuccessNotification(
            response.data.message || "IBAN mis à jour avec succès."
          );

          // Refresh quote data
          this.getQuoteInfo(this.insurance.id);
        }

      } catch (error) {

        const errorData = error.response.data || {};
        const status = error.response.status;

        if (status === 422) {
          this.showErrorNotification(
            errorData.message || "IBAN invalide."
          );
          return;
        }

        this.showErrorNotification(
          errorData.message || "Une erreur est survenue."
        );

      } finally {
        this.isUpdatingIban = false;
      }
    },
    restartFresh() {
      window.location.href = "/garantie-auto-mensualisee-v2";
    },
    changeStep(step = 5) {
      if (!this.insurance || !this.insurance.details || !this.insurance.details.serial_number) {
        this.showErrorNotificationText(
            "Le numéro de série (VIN) du véhicule est manquant.<br>" +
            "Veuillez retourner à l'étape 1, ouvrir le popup <strong>Modifier le véhicule</strong> " +
            "et saisir le Numéro de série (VIN) avant de souscrire à la garantie.",
            null
        );
        return;
      }
      this.mechanicCode != '705223' ? this.getPDF() : this.getPDFWL()
    },
    confirmSubscription() {
      this.$confirm({
        message:
          "Les critères du véhicule ne permettent pas de souscrire à la garantie choisie. Etes-vous sûr de vouloir envoyer un devis sans prix à votre client ?",
        button: {
          no: "Non",
          yes: "Oui"
        },
        /**
         * Callback Function
         * @param {Boolean} confirm
         */
        callback: confirm => {
          if (confirm) {
            this.checkSubscription(this.insurance, this.resendEmail)
          }
        }
      });
    },
    getConfirmationPDF() {
      this.refreshTokenForce()
        .then(response => {
          if (this.getDownloadLinkConfirmationPdf) {
            window.location = this.getDownloadLinkConfirmationPdf;
          } else {
            this.showWarningNotification("Quote Not Found");
          }
        })
        .catch(error => {
          this.showErrorNotification(error);
        });
    },
    closePdfModal() {
      this.pdfModal = false;
    },
    getPDF() {
      if (this.checkEffectiveDate(this.insurance)) {
        this.isBusy = true;
        this.refreshTokenForce()
          .then((response) => {
            if (this.getDownloadLink) {
              fetch(this.getDownloadLink, {
                method: 'GET',
                credentials: 'include'
              })
                .then(() => {
                  console.log('PDF generation triggered');
                })
                .catch(err => {
                  console.error('Error triggering PDF', err);
                });
            } else {
              this.showWarningNotification("Quote Not Found");
            }
          })
          .catch((error) => {
            this.showErrorNotification(error);
          }).finally(() => {
            this.isBusy = false;
            this.$emit('change-step', 5);
          });
      } else {
        this.showWarningNotification(
          "La garantie ne peut-être souscrite à une date antérieur. <br> Veuillez modifier la date de prise d'effet de la garantie."
        );
      }
    },
    getPDFWL() {
      if (this.checkEffectiveDate(this.insurance)) {
        this.isBusy = true;
        this.refreshTokenForce()
          .then((response) => {
            if (this.getDownloadLinkWL) {
              fetch(this.getDownloadLinkWL, {
                method: 'GET',
                credentials: 'include'
              })
                .then(() => {
                  console.log('PDF generation triggered');
                })
                .catch(err => {
                  console.error('Error triggering PDF', err);
                });
            } else {
              this.showWarningNotification("Quote Not Found");
            }
          })
          .catch((error) => {
            this.showErrorNotification(error);
          }).finally(() => {
            this.isBusy = false;
            this.$emit('change-step', 5);
          });
      } else {
        this.showWarningNotification(
          "La garantie ne peut-être souscrite à une date antérieur. <br> Veuillez modifier la date de prise d'effet de la garantie."
        );
      }
    },
    printInsurance() {
      this.ownerExpanded = true;
      this.vehicleExpanded = true;
      this.infoExpanded = true;
      this.guaranteesExpanded = true;
      this.ibanExpanded = true;

      setTimeout(() => {
        window.print();
      }, 300); // allow UI to update
    },
    resendEmail() {
      this.isBusyResend = true;
      let url = "garantie/send-quote-email/" + this.insurance.id;

      this.apiPost(url, {})
        .then(response => {
          this.isBusyResend = false;
          if (response.data.quote && response.data.status == "success") {
            this.showSuccessNotification(response.data.message, "");
            // this.changeStep();
            this.$emit('change-step', 5);
          }
        })
        .catch(error => {
          this.isBusyResend = false;
          this.showErrorNotification(error);
        });
    },
    getQuoteInfo(id) {
      if (!id || id === 'undefined') {
        console.log('Aborting getQuoteInfo: invalid or missing ID');
        return;
      }
      this.isBusy = true;
      let url = "garantie/" + id;
      this.apiGet(url)
        .then((response) => {
          this.insurance = response.data.quote;
          if (response.data.quote && response.data.quote.client.iban) {
            this.insurance.iban = this.formatIbanValue(response.data.quote.client.iban);
          }
          this.numberOfDuplicates = response.data.duplicates;
          this.isBusy = false;
        })
        .catch((error) => {
          this.showErrorNotification(error);
          this.$router.push("/");
        });
    },
    parseValue(
      parsed_value,
      available_options,
      field_id = "ID",
      field_name = "NAME"
    ) {
      for (let value in available_options) {
        if (available_options[value][field_id] === +parsed_value)
          return available_options[value][field_name];
      }
      return "";
    },
    parseCivility(civility) {
      switch (+civility) {
        case 0:
          return "Monsieur";
        case 1:
          return "Mademoiselle";
        case 2:
          return "Madame";
        case 3:
          return "Société";
        case 4:
          return "Monsieur et Madame";
        default:
          return "";
      }
    },
    parseSaleDate(details) {
      let day = details.day_circulation,
        month = details.month_circulation - 1,
        year = details.year_circulation;

      day.length < 2 ? (day = "0" + day.toString()) : "";
      month.length < 2 ? (month = "0" + month.toString()) : "";

      let date = this.moment(new Date()).date(day).month(month).year(year);
      return date.format("DD/MM/YYYY");
    },
    parseBirthDate(birthdate) {
      if (birthdate) {
        return this.moment(birthdate).format("DD/MM/Y");
      }

      return "";
    },
    getFrontPdf(stream = false) {
      this.refreshTokenForce()
        .then(response => {
          if (this.getDownloadLinkCopyPdf) {
            if (stream) {
              window.open(this.getDownloadLinkCopyPdf + '&show_type=stream', '_blank');
            } else {
              window.open(this.getDownloadLinkCopyPdf);
            }
          } else {
            this.showWarningNotification("Quote Not Found");
          }
        })
        .catch(error => {
          this.showErrorNotification(error);
        });
    },
    getFrontPdfWL(stream = false) {
      this.refreshTokenForce()
        .then(response => {
          if (this.getDownloadLinkCopyPdfWL) {
            if (stream) {
              window.open(this.getDownloadLinkCopyPdf + '&show_type=stream', '_blank');
            } else {
              window.open(this.getDownloadLinkCopyPdf);
            }
          } else {
            this.showWarningNotification("Quote Not Found");
          }
        })
        .catch(error => {
          this.showErrorNotification(error);
        });
    },
    closeQuote() {
      if (this.showRetourButton) {
        this.$router.push("/devis");
      } else {
        this.$router.push("/");
      }
    },
    toggleCommissionAccordion() {
      this.showCommissionAccordion = !this.showCommissionAccordion;
      if (this.showCommissionAccordion && !this.commissionFetched) {
        this.fetchInlineCommission();
      }
    },
    cancelInlineCommission() {
      this.inlineMonthlyPrice = this.originalMonthlyPrice;
      this.showCommissionAccordion = false;
    },
    fetchInlineCommission() {
      this.originalMonthlyPrice = this.insurance.monthly_price;
      this.isBusyCommission = true;
      this.apiGet(`garantie/${this.insurance.id}/monthly/get-commission`)
        .then(response => {
          if (response.data && response.data.status === 'success' && response.data.commission) {
            this.commissionSlider = response.data.commission;

            let durationMonths = this.duration_to_months[this.insurance.details.duration] || 48;
            this.commissionSlider.min = 0;
            this.commissionSlider.max = durationMonths * 10;
            this.commissionSlider.step = (durationMonths / 12) * 7.50;

            this.commissionSlider.value = parseFloat(this.commissionSlider.value);
            if (this.commissionSlider.value > this.commissionSlider.max) {
              this.commissionSlider.value = this.commissionSlider.max;
            }
            this.calcInlineMonthlyPrice();
            this.commissionFetched = true;
          }
          this.isBusyCommission = false;
        })
        .catch(error => {
          this.showErrorNotification(error);
          this.isBusyCommission = false;
        });
    },
    calcInlineMonthlyPrice() {
      let monthlyCommission = parseFloat(this.commissionSlider.value) / this.duration_to_months[this.insurance.details.duration];
      this.inlineMonthlyPrice = (Math.round((parseFloat(this.insurance.monthly_price_without_commission) + monthlyCommission) * 100 + 1e-9) / 100).toFixed(2);
    },
    submitInlineCommission() {
      this.isBusyCommissionSubmit = true;
      this.apiPost(`garantie/${this.insurance.id}/monthly/set-custom-commission`, { commission: this.commissionSlider.value })
        .then(response => {
          if (response.data && response.data.status === 'success') {
            this.showSuccessNotification("La commission a été modifiée avec succès.");
            this.showCommissionAccordion = false;
            this.insurance.monthly_price = this.inlineMonthlyPrice;
            this.originalMonthlyPrice = this.inlineMonthlyPrice;
            this.getQuoteInfo(this.insurance.id);
          } else {
            this.showErrorNotification("Une erreur est survenue.");
          }
          this.isBusyCommissionSubmit = false;
        })
        .catch(error => {
          this.showErrorNotification(error);
          this.isBusyCommissionSubmit = false;
        });
    },
    formatIbanValue(value) {
      if (!value) return '';
      let v = value.replace(/[^\dA-Za-z]/g, '').toUpperCase();
      v = v.substring(0, 27);
      return v.replace(/(.{4})/g, '$1 ').trim();
    },
    handleIbanInputView(event) {
      let formatted = this.formatIbanValue(event.target.value);
      this.insurance.iban = formatted;
      event.target.value = formatted;
    },
    formatDisplayIban(iban) {
      if (!iban) return '';
      return this.formatIbanValue(iban);
    }
  },
  mounted() {
    this.getQuoteInfo(this.$route.params.id || this.insuranceData.id);
    this.$eventBus.$emit("sidebar-set-active-tab", "Garantie Auto Mensualisee");

    if (this.$store.state.backToDevisListActive) {
      this.showRetourButton = true;
      this.$store.dispatch("backToDevisDisable");
    }
  },
  beforeDestroy() {
    this.$eventBus.$emit("sidebar-set-active-tab", "");
  },
  watch: {
    pdfWindow: function () {
      window.location.href = "/garantie-auto-mensualisee-v2";
    },
  },
};
</script>


// WEBPACK FOOTER //
// src/components/Garantie/auto-mensualisee/GarantieAutoMensualiseeView.vue